import { useEffect, useRef } from "react";

/**
 * 2px Vivid Pink scroll-progress bar fixed at the very top of the viewport.
 * Pure rAF + transform — no re-renders, respects prefers-reduced-motion
 * (stays visible but no animation curve).
 */
const ScrollProgress = () => {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ticking = false;
    let scrollable = 0;
    const recalc = () => {
      const doc = document.documentElement;
      scrollable = doc.scrollHeight - doc.clientHeight;
    };
    const update = () => {
      const el = barRef.current;
      if (!el) {
        ticking = false;
        return;
      }
      const progress = scrollable > 0 ? window.scrollY / scrollable : 0;
      el.style.transform = `scaleX(${Math.min(1, Math.max(0, progress))})`;
      ticking = false;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    const onResize = () => {
      recalc();
      onScroll();
    };
    recalc();
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    // Recalculate occasionally in case content height changes (lazy images, etc.)
    const interval = window.setInterval(recalc, 2000);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.clearInterval(interval);
    };
  }, []);

  return (
    <div
      aria-hidden
      className="fixed top-0 left-0 right-0 h-[2px] z-[60] pointer-events-none"
      style={{ background: "rgba(10,10,10,0.06)" }}
    >
      <div
        ref={barRef}
        className="h-full origin-left"
        style={{
          background: "linear-gradient(90deg, #FF6B9D 0%, #FFD369 100%)",
          transform: "scaleX(0)",
          transition: "transform 0.08s linear",
          willChange: "transform",
        }}
      />
    </div>
  );
};

export default ScrollProgress;
